import React from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ErrorBoundary from '../Component/ErrorBoundary';

// Pages
import Dashboard from '../Pages/Dashboard';
import CustomerView from '../Pages/Customers/CustomerView';
import Customers from '../Pages/Customers';
import Products from '../Pages/Products';
import SalesRepresentatives from '../Pages/SalesRepresentatives';
import DriverDetail from '../Pages/SalesRepresentatives/DriverDetail';
import LoyaltyPoints from '../Pages/LoyaltyPoints';
import PaymentMethods from '../Pages/PaymentMethods';
import CustomerTypes from '../Pages/CustomerTypes';
import Orders from '../Pages/Orders';
import OrderDetail from '../Pages/Orders/OrderDetail';
import Visits from '../Pages/Visits';
import Offers from '../Pages/Offers';
import OfferForm from '../Pages/Offers/OfferForm';
import InvoiceDetail from '../Pages/Invoices/InvoiceDetail';
import ReceiptDetail from '../Pages/Receipts/ReceiptDetail';
import Users from '../Pages/Users';
import Roles from '../Pages/Roles';
import Permissions from '../Pages/Permissions';
import Models from '../Pages/Models';
import SalesReport from '../Pages/Reports/SalesReport';
import SalesAnalyticsReport from '../Pages/Reports/SalesAnalyticsReport';
import AccountAgreementSettings from '../Pages/AccountAgreement';

// Permission Guard
import PermissionGuard from '../Component/PermissionGuard';

const RouterWrapper: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 24, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <Switch>
          <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
          <Route
            exact
            path="/reports"
            render={() => (
              <PermissionGuard
                menuModelKey="/reports/sales"
                permissions={['sales_report.view', 'sales_report.download']}
              >
                <Redirect to="/reports/sales" />
              </PermissionGuard>
            )}
          />

          <Route
            exact
            path="/dashboard"
            render={() => (
              <PermissionGuard
                permissions={['view_dashboard', 'view_dashboard_sales_reports']}
                requireAll={false}
              >
                <Dashboard />
              </PermissionGuard>
            )}
          />

          <Route
            exact
            path="/reports/sales"
            render={() => (
              <PermissionGuard
                menuModelKey="/reports/sales"
                permissions={['sales_report.view', 'sales_report.download']}
                requireAll={false}
              >
                <SalesReport />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/reports/sales-analytics"
            render={() => (
              <PermissionGuard
                menuModelKey="/reports/sales-analytics"
                permissions={['sales_analytics.view', 'sales_analytics.download']}
                requireAll={false}
              >
                <SalesAnalyticsReport />
              </PermissionGuard>
            )}
          />

          {/* Products - requires view_products permission */}
          <Route
            path="/products"
            render={() => (
              <PermissionGuard permission="view_products">
                <Products />
              </PermissionGuard>
            )}
          />

          {/* Customers - requires view_customers permission */}
          <Route
            exact
            path="/customers"
            render={() => (
              <PermissionGuard permission="view_customers">
                <Customers />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/customers/:id"
            render={() => (
              <PermissionGuard
                permissions={[
                  'view_customer',
                  'view_customers',
                  'customers.view',
                  'customers.read',
                ]}
                requireAll={false}
              >
                <ErrorBoundary>
                  <CustomerView />
                </ErrorBoundary>
              </PermissionGuard>
            )}
          />

          {/* Sales Representatives - requires view_sales_representatives permission */}
          <Route
            exact
            path="/sales-representatives/edit/:id"
            render={() => (
              <PermissionGuard
                permissions={[
                  'update_sales_representative',
                  'sales_representatives.update',
                  'sales_representatives.edit',
                  'drivers.update',
                  'drivers.edit',
                ]}
                requireAll={false}
              >
                <DriverDetail />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/sales-representatives/:id"
            render={() => (
              <PermissionGuard permission="view_sales_representative">
                <DriverDetail />
              </PermissionGuard>
            )}
          />
          <Route
            path="/sales-representatives"
            render={() => (
              <PermissionGuard permission="view_sales_representatives">
                <SalesRepresentatives />
              </PermissionGuard>
            )}
          />

          {/* Orders - requires view_orders permission */}
          <Route
            exact
            path="/orders"
            render={() => (
              <PermissionGuard permission="view_orders">
                <Orders />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/orders/:id"
            render={() => (
              <PermissionGuard permission="view_order">
                <OrderDetail />
              </PermissionGuard>
            )}
          />

          {/* Visits - requires view_visits permission */}
          <Route
            path="/visits"
            render={() => (
              <PermissionGuard permission="view_visits">
                <Visits />
              </PermissionGuard>
            )}
          />

          {/* Offers */}
          <Route
            exact
            path="/offers/new"
            render={() => (
              <PermissionGuard permissions={['create_offer', 'offers.create']} requireAll={false}>
                <OfferForm />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/offers/:id/edit"
            render={() => (
              <PermissionGuard permissions={['update_offer', 'offers.update']} requireAll={false}>
                <OfferForm />
              </PermissionGuard>
            )}
          />
          <Route
            exact
            path="/offers"
            render={() => (
              <PermissionGuard
                permissions={['view_offers', 'view_offer', 'offers.read']}
                requireAll={false}
              >
                <Offers />
              </PermissionGuard>
            )}
          />

          {/* Loyalty Points - requires view_loyalty_points permission */}
          <Route
            path="/loyalty-points"
            render={() => (
              <PermissionGuard permission="view_loyalty_points">
                <LoyaltyPoints />
              </PermissionGuard>
            )}
          />

          {/* Payment Methods - requires any payment_methods permission */}
          <Route
            path="/payment-methods"
            render={() => (
              <PermissionGuard
                permissions={[
                  'manage_payment_methods',
                  'payment_methods.read',
                  'payment_methods.create',
                  'payment_methods.update',
                  'payment_methods.delete',
                ]}
                requireAll={false}
              >
                <PaymentMethods />
              </PermissionGuard>
            )}
          />

          {/* Customer Types - requires any customer_types permission */}
          <Route
            path="/customer-types"
            render={() => (
              <PermissionGuard
                permissions={[
                  'manage_customer_types',
                  'customer_types.read',
                  'customer_types.create',
                  'customer_types.update',
                  'customer_types.delete',
                ]}
                requireAll={false}
              >
                <CustomerTypes />
              </PermissionGuard>
            )}
          />

          {/* Invoices - requires view_invoices permission */}
          <Route
            exact
            path="/invoices/:id"
            render={() => (
              <PermissionGuard permission="view_invoice">
                <InvoiceDetail />
              </PermissionGuard>
            )}
          />

          {/* Receipts */}
          <Route exact path="/receipts/:id" component={ReceiptDetail} />

          {/* Users Management - requires users.view permission */}
          <Route
            path="/users"
            render={() => (
              <PermissionGuard permission="users.view">
                <Users />
              </PermissionGuard>
            )}
          />

          {/* Roles Management - requires roles.view permission */}
          <Route
            path="/roles"
            render={() => (
              <PermissionGuard permission="roles.view">
                <Roles />
              </PermissionGuard>
            )}
          />

          {/* Permissions Management - requires permissions.view permission */}
          <Route
            path="/permissions"
            render={() => (
              <PermissionGuard permission="permissions.view">
                <Permissions />
              </PermissionGuard>
            )}
          />

          {/* Models Management - requires models.view permission */}
          <Route
            path="/models"
            render={() => (
              <PermissionGuard permission="models.view">
                <Models />
              </PermissionGuard>
            )}
          />

          <Route
            path="/account-agreement"
            render={() => (
              <PermissionGuard
                permissions={['update_customer', 'create_customer']}
                requireAll={false}
              >
                <AccountAgreementSettings />
              </PermissionGuard>
            )}
          />

          {/* 404 route */}
          <Route path="*" render={() => <div>Page Not Found</div>} />
        </Switch>
      </motion.div>
    </AnimatePresence>
  );
};

export default RouterWrapper;
